import { yupResolver } from "@hookform/resolvers/yup";
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { LoadingButton } from "@mui/lab";
import { Autocomplete, Box, Checkbox, Dialog, DialogActions, DialogContent, FormControlLabel, FormHelperText, Grid, TextField } from "@mui/material";
import { LocalizationProvider, MobileDatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { useEffect, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { BarrierService } from "../../../../Component/Api/EmploymentEquity/Plan/Barriers/BarrierService";
import { MDBox } from "../../../../Component/Common/Components/MDBox";
import { MDButton } from "../../../../Component/Common/Components/MDButton";
import { MDTypography } from "../../../../Component/Common/Components/MDTypography";
import { Quill } from "../../../../Component/Common/Components/Quill/Quill";
import { EEBarrier } from "../../../../Component/Model/Dto/EmploymentEquity/Plan/EEBarriers";
import { offsetTimezone } from "../../../../Component/Model/helpers";
import { CustomDialogTitle } from "../../../Common/Custom/CustomDialogTitle";
import { NotesTable } from "../../../Common/Notes/NotesTable";
import { SupportingDocuments } from "../../../Common/SupportingDocuments/SupportingDocuments";
import { eePlanBarrierSchema } from "./EEPlanBarrierValidation";
import { getDateWithoutTime } from "../../../../Helpers/DateFormatter";

interface IProps {
    id?: string;
    scorecardId: string;
    link: string;
    previewPopup: boolean;
    handleCancel: () => void;
    refreshTable: () => void;
    setBarrierId: (id: string) => void;
}

export const EEPlanBarrierPeopleModal = (props: IProps) => {
    const [barrierCategory, setBarrierCategory] = useState<EEBarrier.BarrierCategory[]>([]);
    const [barrierId, setBarrierId] = useState<string | undefined>(props.id);
    const [documentsPopUp, setDocumentsPopUp] = useState<boolean>(false);
    const [peopleSearchValue, setPeopleSearchValue] = useState<string>("");

    const barriersMaxLength: number = 1200;
    const scorecardElement = 22;
    const noteCaptureSheet = 36;

    const { control, reset, handleSubmit, formState: { errors, isSubmitting }, setValue, getValues } = useForm<EEBarrier.DtoOut>({
        mode: 'all',
        defaultValues: {
            scorecardId: props.scorecardId,
            policy: false,
            procedure: false,
            practice: false,
            barriers: '',
            affirmativeActionMeasured: '',
            startDate: getDateWithoutTime(new Date()),
            endDate: getDateWithoutTime(new Date()),
            responsibleDesignation: ''
        },
        resolver: yupResolver(eePlanBarrierSchema)
    });

    const handleDataSubmit: SubmitHandler<EEBarrier.DtoOut> = async (barrier: EEBarrier.DtoOut) => {
        barrier.startDate = offsetTimezone(new Date(barrier.startDate.toDateString()));
        barrier.endDate = offsetTimezone(new Date(barrier.endDate.toDateString()));

        try {
            if (!props.id) {
                const result = await BarrierService.CreateBarrier(barrier);
                toast.success("Barrier added succesfully");
                const savedId = result.data.id;
                setBarrierId(savedId);
                setValue("id", savedId);
                props.setBarrierId(savedId);
            }

            else {
                await BarrierService.UpdateBarrier(barrier);
                toast.success("Barrier saved succesfully")
            }

            props.refreshTable();

        } catch (error) {
            console.log(error)
        }

        handleCancel();
    }

    const handleCancel = () => {
        props.handleCancel();
        props.refreshTable();
    }

    const openDocumentsPopUp = () => {
        setDocumentsPopUp(true);
    }

    const getBarrierCategories = async () => {
        try {
            let result = await BarrierService.getBarrierCategories();
            setBarrierCategory(result.data);

        } catch (error) {
            console.log(error)
        }
    }

    const getBarrier = async (id: string) => {
        try {
            const result = await BarrierService.GetBarrier(id);
            var barrier =
                {
                    id: result.data.id,
                    eeBarrierCategoryId: result.data.eeBarrierCategoryId,
                    scorecardId: result.data.scorecardId,
                    policy: result.data.policy,
                    procedure: result.data.procedure,
                    practice: result.data.practice,
                    barriers: result.data.barriers,
                    affirmativeActionMeasured: result.data.affirmativeActionMeasured,
                    startDate: new Date(result.data.startDate),
                    endDate: new Date(result.data.endDate)
                } as EEBarrier.DtoOut
            reset(barrier);
        } catch (error) {
            console.log(error)
        }
    }

    useEffect(() => {
        if (barrierId) {
            getBarrier(barrierId);
        }
        getBarrierCategories();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
    }, [peopleSearchValue]);


    return (
        <Box >
            <Dialog open={props.previewPopup} onClose={handleCancel} aria-labelledby="form-dialog-title" maxWidth='md' fullWidth>
                <CustomDialogTitle id="customized-dialog-title" onClose={handleCancel}>
                    {props.id !== undefined ? "Edit Barrier" : "Add Barrier"}
                </CustomDialogTitle>
                <DialogContent>
                    <Grid container justifyContent='center' sx={{ my: 2 }}>
                        <Grid item xs={10} lg={10}>
                            <Box component="form" id='PlanBarrierForm' onSubmit={handleSubmit(handleDataSubmit)}>
                                <MDBox>
                                    <MDBox mb={1}>
                                        <Grid item xs={12}>
                                            <MDBox mb={2}>
                                                <Controller
                                                    control={control}
                                                    name="eeBarrierCategoryId"
                                                    render={({ field: { onChange, value } }) => (
                                                        <Autocomplete
                                                            options={barrierCategory}
                                                            isOptionEqualToValue={(option, value) => option.id === value.id}
                                                            value={barrierCategory.find(x => x.id === value) ?? null}
                                                            getOptionLabel={option => option.description}
                                                            onChange={(e: any, newValue: any) => {
                                                                onChange(newValue?.id);
                                                                setValue("eeBarrierCategoryId", newValue?.id);
                                                            }}
                                                            renderOption={(props, option) => {
                                                                return (
                                                                    <li {...props} key={option.id}>
                                                                        {option.description}
                                                                    </li>
                                                                );
                                                            }}
                                                            renderInput={(params) =>
                                                                <TextField label='Category *' {...params} type='text'
                                                                    error={!!errors.eeBarrierCategoryId}
                                                                    helperText={errors.eeBarrierCategoryId?.message}
                                                                    variant="standard"
                                                                />
                                                            }
                                                        />
                                                    )}
                                                />
                                            </MDBox>
                                            <MDBox mb={2}>
                                                <Controller
                                                    control={control}
                                                    name="policy"
                                                    render={({ field: { onChange, value } }) => (
                                                        <FormControlLabel control={<Checkbox
                                                            checked={value}
                                                            onChange={(e: any) => {
                                                                onChange(e.target.checked);
                                                                setValue("policy", e.target.checked);
                                                            }}
                                                            color="secondary"
                                                        />} label="Policy" />
                                                    )}
                                                />
                                                <Controller
                                                    control={control}
                                                    name="procedure"
                                                    render={({ field: { onChange, value } }) => (
                                                        <FormControlLabel control={<Checkbox
                                                            size="small"
                                                            checked={value}
                                                            onChange={(e: any) => {
                                                                onChange(e.target.checked);
                                                                setValue("procedure", e.target.checked);
                                                            }}
                                                            color="secondary"
                                                        />} label="Procedure" />
                                                    )}
                                                />
                                                <Controller
                                                    control={control}
                                                    name="practice"
                                                    render={({ field: { onChange, value } }) => (
                                                        <FormControlLabel control={<Checkbox
                                                            checked={value}
                                                            onChange={(e: any) => {
                                                                onChange(e.target.checked);
                                                                setValue("practice", e.target.checked);
                                                            }}
                                                            color="secondary"
                                                        />} label="Practice" />
                                                    )}
                                                />
                                            </MDBox>
                                            <MDBox mb={2} className="leftAlign">
                                                <MDTypography
                                                    variant='button'
                                                    fontWeight='regular'
                                                    style={{ color: '#666666', fontSize: "0.85em" }}
                                                >
                                                    Barriers * (Max 1200 characters)
                                                </MDTypography>
                                                <Controller
                                                    control={control}
                                                    name="barriers"
                                                    render={({ field: { onChange, value } }) => (
                                                        <Quill initialContent={value}
                                                            handleChange={(newValue: string) => {
                                                                onChange(newValue);
                                                                setValue('barriers', newValue)
                                                            }}
                                                            maxLength={barriersMaxLength}>
                                                        </Quill>
                                                    )}
                                                />
                                                {(!!errors.barriers) && <FormHelperText error>{errors.barriers?.message}</FormHelperText>}
                                            </MDBox>
                                            <MDBox mb={2} className="leftAlign">
                                                <MDTypography
                                                    variant='button'
                                                    fontWeight='regular'
                                                    style={{ color: '#666666', fontSize: "0.85em" }}
                                                >
                                                    Affirmative Action Measures * (Max 1200 characters)
                                                </MDTypography>
                                                <Controller
                                                    control={control}
                                                    name="affirmativeActionMeasured"
                                                    render={({ field: { onChange, value } }) => (
                                                        <Quill initialContent={value}
                                                            handleChange={(newValue: string) => {
                                                                onChange(newValue);
                                                                setValue('affirmativeActionMeasured', newValue)
                                                            }}
                                                            maxLength={barriersMaxLength}>
                                                        </Quill>
                                                    )}
                                                />
                                                {(!!errors.affirmativeActionMeasured) && <FormHelperText error>{errors.affirmativeActionMeasured?.message}</FormHelperText>}
                                            </MDBox>
                                            <MDBox mb={2}>
                                                <MDTypography
                                                    variant='button'
                                                    fontWeight='regular'
                                                    style={{ color: '#666666', fontSize: "0.85em" }}
                                                >
                                                    Time Frame
                                                </MDTypography>
                                                <Grid container style={{ paddingTop: '20px' }}>
                                                    <Grid item xs={12} sm={6} md={6}>
                                                        <MDBox mb={2}>
                                                            <LocalizationProvider dateAdapter={AdapterDateFns}>
                                                                <Controller
                                                                    control={control}
                                                                    name="startDate"
                                                                    render={({ field: { onChange, value } }) => (
                                                                        <MobileDatePicker
                                                                            inputFormat="yyyy/MM/dd"
                                                                            mask="____/__/__"
                                                                            label="From *"
                                                                            value={value}
                                                                            onChange={(date) => {
                                                                                if (date != null) {
                                                                                    onChange(getDateWithoutTime(date));
                                                                                    setValue("startDate", getDateWithoutTime(date));
                                                                                }
                                                                            }}
                                                                            renderInput={(params) => <TextField {...params}
                                                                                error={!!errors.startDate}
                                                                                helperText={errors.startDate?.message}
                                                                            />
                                                                            }
                                                                            views={['year', 'month', 'day']}
                                                                            maxDate={getValues("endDate")}
                                                                        />
                                                                    )}
                                                                />
                                                            </LocalizationProvider>
                                                        </MDBox>
                                                    </Grid>
                                                    <Grid item xs={12} sm={6} md={6}>
                                                        <MDBox mb={2} >
                                                            <LocalizationProvider dateAdapter={AdapterDateFns}>
                                                                <Controller
                                                                    control={control}
                                                                    name="endDate"
                                                                    render={({ field: { onChange, value } }) => (
                                                                        <MobileDatePicker
                                                                            inputFormat="yyyy/MM/dd"
                                                                            mask="____/__/__"
                                                                            label="To *"
                                                                            value={value}
                                                                            onChange={(newDate) => {
                                                                                if (newDate != null) {
                                                                                    onChange(getDateWithoutTime(newDate));
                                                                                    setValue("endDate", getDateWithoutTime(newDate));
                                                                                }
                                                                            }}
                                                                            renderInput={(params) => <TextField {...params}
                                                                                error={!!errors.endDate}
                                                                                helperText={errors.endDate?.message}
                                                                            />
                                                                            }
                                                                            views={['year', 'month', 'day']}
                                                                            minDate={getValues("startDate")}
                                                                        />
                                                                    )}
                                                                />
                                                            </LocalizationProvider>
                                                        </MDBox>
                                                    </Grid>
                                                </Grid>
                                            </MDBox>
                                            <MDBox mb={2}>
                                                <Controller
                                                    control={control}
                                                    name="responsibleDesignation"
                                                    render={({ field }) => (
                                                        <TextField
                                                            {...field} 
                                                            id="responsibleDesignation"
                                                            label="Responsible Designation"
                                                            variant="outlined"
                                                            error={!!errors.responsibleDesignation}
                                                            helperText={errors.responsibleDesignation?.message}
                                                            sx={{ width: '100%' }}
                                                        />
                                                    )}
                                                />
                                            </MDBox>
                                        </Grid>
                                    </MDBox>
                                </MDBox>
                            </Box>
                            {barrierId !== undefined && <MDBox style={{ marginLeft: '-40px', marginRight: '-40px' }}>
                                <NotesTable scorecardElement={scorecardElement} noteCaptureSheet={noteCaptureSheet} linkedEntityId={barrierId} scorecardId={props.scorecardId}></NotesTable>
                            </MDBox>}
                        </Grid>
                    </Grid>
                    <MDBox mt={3}  >
                        <MDBox
                            pr={3}
                            display='flex'
                            justifyContent='right'
                        >
                        </MDBox>
                    </MDBox>
                    {barrierId !== undefined && <SupportingDocuments display={documentsPopUp} handleCancel={() => setDocumentsPopUp(false)} link={props.link} id={barrierId} scorecardId={props.scorecardId} isEEModule={true} />}
                </DialogContent>
                <DialogActions>
                    {barrierId !== undefined &&
                        <MDButton variant="contained" color="primary" onClick={openDocumentsPopUp} startIcon={<AttachFileIcon />}
                            size="medium">
                            Attach File
                        </MDButton>
                    }
                    <LoadingButton variant='contained' color='primary' form='PlanBarrierForm' type='submit' loading={isSubmitting}>
                        Save
                    </LoadingButton>
                </DialogActions>
            </Dialog>
        </Box>);
}
